// FacturaPage.jsx
import { fetchWithAuth } from "../components/authFetch";
import React, { useEffect, useState } from "react";
import BarraFiltros, { SinResultados } from "./BarraFiltros";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  IconButton
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoice, faPlus, faEye } from "@fortawesome/free-solid-svg-icons";

const API = process.env.REACT_APP_API_URL || "https://api.hmsaa.com.mx:5001";
const API_BASE = `${API}/api`;

export default function FacturaPage() {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openModal, setOpenModal] = useState(false);
  const [xmlFile, setXmlFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [subiendo, setSubiendo] = useState(false);

  const [referencia, setReferencia] = useState(""); // ✅ NUEVO

  // Filtros de la lista
  const [buscar, setBuscar] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [pdfFiltro, setPdfFiltro] = useState("");

  const [openConceptosModal, setOpenConceptosModal] = useState(false);
  const [conceptos, setConceptos] = useState([]);

  const theme = useTheme();

  // ======================================================
  // VER PDF
  // ======================================================
  const verFacturaPdf = async (factura) => {
    try {
      const token = localStorage.getItem("access_token");

      const res = await fetch(`${API_BASE}/facturas/${factura.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        Swal.fire("Error", "No se pudo cargar el PDF", "error");
        return;
      }

      const blob = await res.blob();
      const fileURL = URL.createObjectURL(blob);

      Swal.fire({
        title: "Vista previa de la factura",
        html: `
          <div style="height:75vh">
            <iframe
              src="${fileURL}"
              style="width:100%; height:100%; border:none;"
            ></iframe>
          </div>
        `,
        width: "85%",
        showCancelButton: true,
        confirmButtonText: "Descargar",
        cancelButtonText: "Cerrar",
        didClose: () => {
          URL.revokeObjectURL(fileURL);
        }
      }).then((result) => {
        if (result.isConfirmed) {
          const a = document.createElement("a");
          a.href = fileURL;
          a.download = `factura_${factura.id}.pdf`;
          a.click();
        }
      });
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Error al abrir el PDF", "error");
    }
  };

  // ======================================================
  // GET FACTURAS
  // ======================================================
  const fetchFacturas = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE}/facturas`);
      if (!data) {
        setLoading(false);
        return;
      }

      setFacturas(
        data.map((f) => ({
          id: f.id_factura,
          uuid: f.uuid,
          referencia: f.referencia, // ✅ NUEVO
          fecha: f.fecha,
          nombre_emisor: f.emisor,
          total: f.total,
          tiene_pdf: f.tiene_pdf
        }))
      );
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudieron cargar las facturas", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFacturas();
  }, []);

  // ======================================================
  // VALIDACIONES ARCHIVOS
  // ======================================================
  const handleXmlChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".xml")) {
      Swal.fire("Archivo inválido", "Solo se permiten XML", "error");
      e.target.value = null;
      return;
    }
    setXmlFile(file);
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      Swal.fire("Archivo inválido", "Solo se permiten PDF", "error");
      e.target.value = null;
      return;
    }
    setPdfFile(file);
  };

  // ======================================================
  // CREAR FACTURA
  // ======================================================
  const handleCrearFactura = async () => {
    if (!xmlFile) {
      Swal.fire("Error", "Selecciona un XML", "error");
      return;
    }

    setSubiendo(true);

    try {
      const token = localStorage.getItem("access_token");

      // ---------- SUBIR XML ----------
      const xmlForm = new FormData();
      xmlForm.append("file", xmlFile);
      xmlForm.append("referencia", referencia); // ✅ NUEVO

      const resXml = await fetch(`${API_BASE}/facturas/scan-xml`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: xmlForm
      });

      const dataXml = await resXml.json();

      if (!resXml.ok) {
        Swal.fire("Error", dataXml.error || "Error al subir XML", "error");
        return;
      }

      const facturaId = dataXml.factura_id;

      // ---------- SUBIR PDF ----------
      if (pdfFile) {
        const pdfForm = new FormData();
        pdfForm.append("file", pdfFile);

        await fetch(`${API_BASE}/facturas/${facturaId}/upload-pdf`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: pdfForm
        });
      }

      Swal.fire("Éxito", "Factura creada", "success");
      setOpenModal(false);
      setXmlFile(null);
      setPdfFile(null);
      setReferencia(""); // ✅ NUEVO
      fetchFacturas();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Error al crear factura", "error");
    } finally {
      setSubiendo(false);
    }
  };

  // ======================================================
  // VER CONCEPTOS (TODOS + RELACIONES)
  // ======================================================
  const verConceptos = async (factura) => {
    try {
      const data = await fetchWithAuth(`${API_BASE}/facturas/${factura.id}/conceptos`);
      if (!data) return;

      setConceptos(data);
      setOpenConceptosModal(true);
    } catch {
      Swal.fire("Error", "No se pudieron cargar los conceptos", "error");
    }
  };

  // ======================================================
  // RENDER
  // ======================================================
  const termino = buscar.trim().toLowerCase();

  const facturasFiltradas = facturas.filter((f) => {
    if (pdfFiltro && (pdfFiltro === "con") !== Boolean(f.tiene_pdf)) return false;
    if (desde && f.fecha < desde) return false;
    if (hasta && f.fecha > hasta) return false;

    return [f.referencia, f.uuid, f.nombre_emisor].some((v) =>
      (v ?? "").toString().toLowerCase().includes(termino)
    );
  });

  const hayFiltros = termino !== "" || desde !== "" || hasta !== "" || pdfFiltro !== "";

  const totalMostrado = facturasFiltradas.reduce((acc, f) => acc + (Number(f.total) || 0), 0);

  const textoFacturas = facturas.length === 1 ? "factura" : "facturas";
  const textoCantidad =
    hayFiltros && facturasFiltradas.length !== facturas.length
      ? `${facturasFiltradas.length} de ${facturas.length}`
      : `${facturas.length}`;
  const textoContador = `${textoCantidad} ${textoFacturas} · Total ${totalMostrado.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN"
  })}`;

  const limpiarFiltros = () => {
    setBuscar("");
    setDesde("");
    setHasta("");
    setPdfFiltro("");
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
        <Typography>Cargando facturas…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Typography sx={{ flexGrow: 1 }} variant="h6">
            Facturas
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setOpenModal(true)}
          >
            Nueva factura
          </Button>
        </Toolbar>
      </AppBar>

      <BarraFiltros
        busqueda={buscar}
        onBusqueda={setBuscar}
        placeholder="Buscar por referencia, UUID o emisor"
        filtros={[
          {
            id: "pdf",
            tipo: "chips",
            etiqueta: "PDF",
            valor: pdfFiltro,
            onChange: setPdfFiltro,
            opciones: [
              { valor: "", etiqueta: "Todos" },
              { valor: "con", etiqueta: "Con PDF" },
              { valor: "sin", etiqueta: "Sin PDF" }
            ]
          },
          { id: "desde", tipo: "fecha", etiqueta: "Desde", valor: desde, onChange: setDesde },
          { id: "hasta", tipo: "fecha", etiqueta: "Hasta", valor: hasta, onChange: setHasta }
        ]}
        contador={textoContador}
        onLimpiar={limpiarFiltros}
      />

      {/* TABLA */}
      <Box>
        <TableContainer component={Paper} sx={{ margin: "20px auto", maxWidth: "90%" }}>
          <Table>
            <TableHead>
              <TableRow>
                {["Referencia", "UUID", "Fecha", "Emisor", "Total", "Acciones"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      fontWeight: "bold"
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {facturasFiltradas.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{f.referencia || "—"}</TableCell>
                  <TableCell>{f.uuid}</TableCell>
                  <TableCell>{f.fecha}</TableCell>
                  <TableCell>{f.nombre_emisor}</TableCell>
                  <TableCell>
                    {Number(f.total).toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN"
                    })}
                  </TableCell>

                  <TableCell align="right">
                    <IconButton onClick={() => verConceptos(f)}>
                      <FontAwesomeIcon icon={faFileInvoice} />
                    </IconButton>

                    <IconButton disabled={!f.tiene_pdf} onClick={() => verFacturaPdf(f)}>
                      <FontAwesomeIcon icon={faEye} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {facturasFiltradas.length === 0 && (
                <SinResultados
                  colSpan={6}
                  total={facturas.length}
                  onLimpiar={limpiarFiltros}
                  entidad="facturas"
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* MODAL CREAR */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva factura</DialogTitle>

        <DialogContent>
          <TextField
            label="Referencia (opcional)"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
          />

          <Typography>XML</Typography>
          <Input type="file" onChange={handleXmlChange} />

          <Box mt={2}>
            <Typography>PDF (opcional)</Typography>
            <Input type="file" onChange={handlePdfChange} />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button onClick={handleCrearFactura} disabled={subiendo}>
            {subiendo ? "Guardando..." : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL CONCEPTOS */}
      <Dialog
        open={openConceptosModal}
        onClose={() => setOpenConceptosModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Conceptos</DialogTitle>

        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Descripción</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Asignado</TableCell>
                <TableCell>Disponible</TableCell>
                <TableCell>Importe</TableCell>
                <TableCell>Relacionado a</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {conceptos.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.descripcion}</TableCell>
                  <TableCell>{c.cantidad_total}</TableCell>
                  <TableCell>{c.cantidad_asignada}</TableCell>
                  <TableCell>{c.disponibles}</TableCell>

                  <TableCell>
                    {c.importe == null
                      ? "—"
                      : Number(c.importe).toLocaleString("es-MX", {
                          style: "currency",
                          currency: "MXN"
                        })}
                  </TableCell>

                  <TableCell>
                    {/* PRODUCTOS */}
                    {c.asignaciones?.productos?.length ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Productos:
                        </Typography>
                        {c.asignaciones.productos.map((p) => (
                          <Typography key={`p-${p.id_asignacion}`} variant="body2">
                            {p.producto.numero_serie} — {p.producto.descripcion}
                            {p.cantidad > 1 ? ` (x${p.cantidad})` : ""}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}

                    {/* SERVICIOS */}
                    {c.asignaciones?.servicios?.length ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Servicios:
                        </Typography>
                        {c.asignaciones.servicios.map((s) => (
                          <Typography key={`s-${s.id_asignacion}`} variant="body2">
                            {s.servicio.nombre}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}

                    {/* NADA */}
                    {!c.asignaciones?.productos?.length &&
                      !c.asignaciones?.servicios?.length && (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                          — Sin asignar —
                        </Typography>
                      )}
                  </TableCell>
                </TableRow>
              ))}

              {conceptos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography sx={{ opacity: 0.7 }}>
                      Esta factura no tiene conceptos.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenConceptosModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
